import React, {Component} from 'react';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import MuiButton from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Alert from '@material-ui/lab/Alert';
import Button from '../../../components/button';

/**
 * Mockup screen S7's activation half — "Activate cover".
 *
 * IT LIVES HERE, ON /settings/access, AND NOT BESIDE THE NOMINATION IT ACTIVATES. §19's Q7 puts
 * `access.delegation:activate` on `platform_admin` alone, and `secured-layout.js:75-87` exempts
 * only `/user-profile-admin` and `/settings/access` from the context redirect — every other
 * `/settings/*` path bounces a user whose `getContext()` is null, which is the normal state of a
 * platform administrator. On `/settings/delegations` this panel would have been unreachable by the
 * one role permitted to use it: 4b §3.9's defect, by a different route.
 *
 * The mockup's drawn label "Who can activate: Benefits team lead" is SUPERSEDED by Q7. It predates
 * that decision by five weeks.
 */
class NominationPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {target: null, reason: '', until: ''};
  }

  open = (nomination) => this.setState({target: nomination, reason: '', until: ''});

  close = () => this.setState({target: null});

  submit = () => {
    const {target, reason, until} = this.state;
    if (!target || !reason.trim()) return;
    this.props.onActivate(target.nominationId, {
      reason: reason.trim(),
      expiresAt: until ? `${until}T23:59:59` : null,
    });
    this.setState({target: null});
  };

  render() {
    const {nominations, error} = this.props;
    const {target, reason, until} = this.state;
    const canSubmit = Boolean(target) && Boolean(reason.trim());

    return (
      <div className="rounded-card nomination-panel" data-testid="ac-admin-nominations">
        <h3>Standing nominations</h3>
        <p className="panel-sub">
          Dormant cover, waiting to be switched on. Activating one grants the nominee the duties
          below for the window you set — the nominator consented in advance, which is what makes
          this something other than helping yourself to their access.
        </p>

        <Table size="small" className="data-table">
          <TableHead>
            <TableRow>
              <TableCell><strong>Nominator</strong></TableCell>
              <TableCell><strong>Backup</strong></TableCell>
              <TableCell><strong>Duties</strong></TableCell>
              <TableCell><strong>Set on</strong></TableCell>
              <TableCell><strong>Action</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!nominations.length && (
              <TableRow>
                <TableCell colSpan={5} className="muted" data-testid="ac-nominations-empty">
                  No dormant nominations. Nobody has named a backup yet.
                </TableCell>
              </TableRow>
            )}
            {nominations.map((n) => (
              <TableRow key={n.nominationId} hover data-testid="ac-admin-nomination-row">
                <TableCell>{n.nominatorName || n.nominatorSubjectId}</TableCell>
                <TableCell>{n.nomineeName || n.nomineeSubjectId}</TableCell>
                <TableCell>
                  {n.pairs.map((p, i) => (
                    <div key={`${p.code}-${i}`} className="pair-line">
                      <code>{p.code}</code>{' '}
                      <span className="muted">
                        {p.scope.type === 'global' ? 'everywhere' : `${p.scope.type} · ${p.scope.id}`}
                      </span>
                    </div>
                  ))}
                </TableCell>
                <TableCell>{new Date(n.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Button label="Activate cover" className="btn btn-action"
                    onClick={() => this.open(n)} data-testid="ac-activate-open" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Dialog open={Boolean(target)} onClose={this.close} maxWidth="sm" fullWidth
          PaperProps={{'className': 'ac-activate-dialog', 'data-testid': 'ac-activate-dialog'}}>
          <DialogTitle>Activate cover</DialogTitle>
          <DialogContent>
            {target && (
              <p className="dialog-lede">
                <strong>{target.nominatorName || target.nominatorSubjectId}</strong> is unavailable.
                Their nomination names <strong>{target.nomineeName || target.nomineeSubjectId}</strong>.
                Activating grants them {target.pairs.length}{' '}
                {target.pairs.length === 1 ? 'duty' : 'duties'} for the window you set.
                They are notified and can end it at any time.
              </p>
            )}

            <TextField
              label="Reason"
              required
              fullWidth
              multiline
              minRows={2}
              variant="outlined"
              value={reason}
              onChange={(e) => this.setState({reason: e.target.value})}
              helperText="Recorded on the audit row. Activation is an act taken on someone else's behalf without asking them."
              inputProps={{'maxLength': 255, 'data-testid': 'ac-activate-reason'}}
            />

            <TextField
              label="Until"
              type="date"
              fullWidth
              value={until}
              onChange={(e) => this.setState({until: e.target.value})}
              InputLabelProps={{shrink: true}}
              /* The clamp is the model's, not the form's — it takes min(requested, 90 days, the
                 nominator's own backing), so a date beyond any of those is quietly pulled in and
                 reported back rather than rejected here. */
              helperText="Capped at 90 days, and never beyond what the nominator's own access allows."
              inputProps={{'data-testid': 'ac-activate-until'}}
              style={{marginTop: 18}}
            />

            <div className="dialog-note">
              Cover does not renew itself. If the absence continues, someone must consciously
              extend it.
            </div>

            {error && <Alert severity="error" data-testid="ac-activate-error">{error}</Alert>}
          </DialogContent>
          <DialogActions>
            <MuiButton onClick={this.close} data-testid="ac-activate-cancel">Cancel</MuiButton>
            <MuiButton color="primary" variant="contained" disabled={!canSubmit}
              onClick={this.submit} data-testid="ac-activate-submit">
              Activate cover
            </MuiButton>
          </DialogActions>
        </Dialog>

        <div className="panel-note">
          {/* Not a UI rule — R2's. A nominee activating their own nomination grows their own
              effective set, and the post-condition rolls the whole write back. Stated here so the
              refusal is not surprising when it happens. */}
          Nobody can activate their own nomination. Attempting it is refused and logged, because
          activating it would increase your own access — and nobody may do that on their own.
        </div>
      </div>
    );
  }
}

export default NominationPanel;
